/**
 * TanStack Query хуки для авторизации и управления сессией.
 */

import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	acceptInvite,
	changePassword,
	checkEmail,
	getInvitationInfo,
	getSession,
	getSSOLoginUrl,
	resendVerification,
	restorePassword,
	restorePasswordRequest,
	signIn,
	signOut,
	signUp,
	signUpAws,
	connectAws,
	verifyEmail,
} from '@/api/auth';
import { authorityUnavailableError, getToken, sessionUnknownToServer } from '@/api/client';
import type {
	AcceptInviteParams,
	ChangePasswordParams,
	RestorePasswordParams,
	SessionResponse,
	SignUpAwsParams,
	SignUpParams,
} from '@/api/types';
import { useFeaturesStore } from '@/stores/features';
import { useSessionStore } from '@/stores/session';
import { SILENT_ERROR } from '@/lib/mutationMeta';

/** Ключ кеша сессии; его перечитывают после изменения ролей (useTeam) */
export const SESSION_QUERY_KEY = ['session'] as const;

/** Вход в систему */
export function useSignIn() {
	const queryClient = useQueryClient();

	return useMutation({
		meta: SILENT_ERROR,
		mutationFn: ({ login, password }: { login: string; password: string }) =>
			signIn(login, password),
		onSuccess: ({ user, token }) => {
			const store = useSessionStore.getState();
			store.setSession(user, token);

			const prevPath = store.prevPath;
			if (prevPath) {
				store.setPrevPath('');
				window.location.href = prevPath;
			}

			queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
		},
	});
}

/** Регистрация: обычная или по подписке AWS Marketplace (параметры из ссылки Marketplace) */
export interface SignUpRequest {
	params: SignUpParams;
	aws?: Omit<SignUpAwsParams, 'login' | 'password'>;
}

/** Открывает сессию, выданную сервером: токен из заголовка ответа уже сохранил клиент API */
function openIssuedSession(session: SessionResponse | null, queryClient: QueryClient): void {
	const token = getToken();
	if (session && token) {
		useSessionStore.getState().setSession(session.client, token);
		void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
	}
}

/**
 * Регистрация. Результат null — сервер ждёт подтверждения email и сессии не выдал:
 * страница просит проверить почту, а не пытается войти (вход дал бы 403 EMAIL_UNVERIFIED).
 */
export function useSignUp() {
	const queryClient = useQueryClient();

	return useMutation({
		meta: SILENT_ERROR,
		mutationFn: (request: SignUpRequest) =>
			request.aws && useFeaturesStore.getState().isEnabled('awsIntegration')
				? signUpAws({ ...request.params, ...request.aws })
				: signUp(request.params),
		onSuccess: (session) => openIssuedSession(session, queryClient),
	});
}

/** Привязка подписки AWS Marketplace к существующей учётной записи; сервер открывает сессию */
export function useConnectAws() {
	const queryClient = useQueryClient();

	return useMutation({
		meta: SILENT_ERROR,
		mutationFn: (params: SignUpAwsParams) => {
			if (!useFeaturesStore.getState().isEnabled('awsIntegration'))
				throw new Error('Feature disabled');
			return connectAws(params);
		},
		onSuccess: (session) => openIssuedSession(session, queryClient),
	});
}

/** Подтверждение email по секрету из ссылки: запрос уходит один раз и не повторяется сам */
export function useVerifyEmail(secret: string) {
	const queryClient = useQueryClient();

	return useQuery({
		queryKey: ['email-verification', secret],
		queryFn: async () => {
			const session = await verifyEmail(secret);
			openIssuedSession(session, queryClient);
			return session;
		},
		retry: false,
		staleTime: Infinity,
		gcTime: Infinity,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
}

/** Повторное письмо подтверждения email */
export function useResendVerification() {
	return useMutation({
		meta: SILENT_ERROR,
		mutationFn: (email: string) => resendVerification(email),
	});
}

/** Принятие приглашения */
export function useAcceptInvite() {
	const queryClient = useQueryClient();

	return useMutation({
		meta: SILENT_ERROR,
		mutationFn: (params: AcceptInviteParams) => acceptInvite(params),
		onSuccess: (data) => {
			const token = getToken();
			if (token) {
				useSessionStore.getState().setSession(data.client, token);
			}
			queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
		},
	});
}

/** Сведения приглашения по токену из ссылки; без токена запрос не выполняется */
export function useInvitationInfo(token: string | null) {
	return useQuery({
		queryKey: ['invitation', token],
		queryFn: () => getInvitationInfo(token ?? ''),
		enabled: Boolean(token),
		retry: false,
	});
}

/**
 * Выход из системы. Сервер, который сессии запроса уже не знает (sessionUnknownToServer),
 * подтверждает выход: он состоялся, окно повторного входа и уведомление об отказе не
 * нужны. Другая ошибка выход не подтверждает — сессия на сервере жива, локально она не
 * сбрасывается, а отказ показывает общее уведомление.
 */
export function useSignOut() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			try {
				await signOut();
			} catch (error) {
				if (!sessionUnknownToServer(error)) throw error;
			}
		},
		onSuccess: () => {
			useSessionStore.getState().clearSession();
			queryClient.removeQueries({ queryKey: SESSION_QUERY_KEY });
		},
	});
}

/** Проверка текущей сессии (при маунте приложения) */
export function useSession() {
	return useQuery({
		queryKey: SESSION_QUERY_KEY,
		queryFn: async () => {
			const generation = useSessionStore.getState().generation;
			const data = await getSession();
			/* clear/cancel кеша не прерывают побочный эффект уже выполняющегося queryFn */
			const store = useSessionStore.getState();
			if (store.generation !== generation) throw authorityUnavailableError();
			const token = getToken();
			if (token && !store.restoreSession(data.client, token, generation)) {
				throw authorityUnavailableError();
			}
			return data;
		},
		staleTime: Infinity,
		retry: false,
	});
}

/** Проверка email (SSO / AWS статус) */
export function useCheckEmail() {
	return useMutation({
		meta: SILENT_ERROR,
		mutationFn: (email: string) => checkEmail(email),
	});
}

/** Получение URL для SSO входа */
export function useGetSSOLoginUrl() {
	return useMutation({
		meta: SILENT_ERROR,
		mutationFn: (email: string) => getSSOLoginUrl(email),
	});
}

/** Запрос email для сброса пароля */
export function useRestorePasswordRequest() {
	return useMutation({
		meta: SILENT_ERROR,
		mutationFn: (email: string) => restorePasswordRequest(email),
	});
}

/** Сброс пароля по токену */
export function useRestorePassword() {
	const queryClient = useQueryClient();

	return useMutation({
		meta: SILENT_ERROR,
		mutationFn: (params: RestorePasswordParams) => restorePassword(params),
		onSuccess: (session) => openIssuedSession(session, queryClient),
	});
}

/** Смена пароля авторизованным пользователем: отказ показывает форма, итог — уведомление */
export function useChangePassword() {
	return useMutation({
		meta: { ...SILENT_ERROR, successMessage: { messageKey: 'auth:changePassword.success' } },
		mutationFn: (params: ChangePasswordParams) => changePassword(params),
	});
}
